import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken, removeAuthToken, getAuthToken } from '../services/api';
import paymentAPI from '../services/paymentApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = () => {
      try {
        const savedToken = getAuthToken();
        const savedUser = localStorage.getItem('user_data');
        
        console.log('AuthContext initializing:', {
          hasToken: !!savedToken,
          hasUser: !!savedUser,
          tokenLength: savedToken?.length || 0,
          savedToken: savedToken?.substring(0, 20) + '...',
          savedUserData: savedUser?.substring(0, 50) + '...'
        });
        
        if (savedToken && savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            
            // Validate that the user object has required fields
            if (parsedUser && parsedUser.id && parsedUser.email) {
              console.log('Setting auth state with valid data:', {
                userId: parsedUser.id,
                email: parsedUser.email,
                tokenPrefix: savedToken.substring(0, 10) + '...'
              });
              
              // Set auth state atomically
              setUser(parsedUser);
              setToken(savedToken);
              
              // Ensure API headers are set
              setAuthToken(savedToken);
              
              console.log('✅ Authentication restored successfully');
            } else {
              console.error('Invalid user data structure:', parsedUser);
              throw new Error('Invalid user data structure');
            }
          } catch (parseError) {
            console.error('Error parsing saved user data:', parseError);
            // Clear corrupted data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            setUser(null);
            setToken(null);
          }
        } else {
          console.log('No saved authentication found - setting null state');
          setUser(null);
          setToken(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
        setToken(null);
      } finally {
        console.log('AuthContext initialization complete, setting loading = false');
        setLoading(false);
      }
    };

    // Small delay to ensure localStorage is ready
    const timeoutId = setTimeout(initAuth, 10);
    return () => clearTimeout(timeoutId);
  }, []);

  // Process OAuth session if present in URL
  useEffect(() => {
    const processOAuthSession = async () => {
      const hash = window.location.hash;
      if (!hash) return;

      const sessionMatch = hash.match(/session_id=([^&]+)/);
      if (!sessionMatch) return;

      const sessionId = sessionMatch[1];
      console.log('🔐 Processing OAuth session...');

      try {
        setLoading(true);
        
        // Call backend to process session
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': sessionId
          }
        });

        if (!response.ok) {
          throw new Error('Failed to process OAuth session');
        }

        const data = await response.json();
        console.log('✅ OAuth session processed:', data.user.email);

        // Set user state (session token is in httpOnly cookie)
        setUser(data.user);
        localStorage.setItem('user_data', JSON.stringify(data.user));
        
        // Also store in token for API calls via Authorization header
        setAuthToken(data.session_token);
        setToken(data.session_token);

        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);

      } catch (error) {
        console.error('❌ OAuth session processing failed:', error);
      } finally {
        setLoading(false);
      }
    };

    processOAuthSession();
  }, []);

  // Add effect to monitor token changes in localStorage (for debugging)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token' || e.key === 'user_data') {
        console.log('localStorage auth change detected:', {
          key: e.key,
          oldValue: !!e.oldValue,
          newValue: !!e.newValue
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);
      
      const { access_token, user: userData } = response;
      
      setAuthToken(access_token);
      localStorage.setItem('user_data', JSON.stringify(userData));
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      
      const { access_token, user: newUser } = response;
      
      setAuthToken(access_token);
      localStorage.setItem('user_data', JSON.stringify(newUser));
      
      setToken(access_token);
      setUser(newUser);
      
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('Logging out user');
    removeAuthToken();
    setToken(null);
    setUser(null);
    
    // Clear any other auth-related data
    localStorage.removeItem('user_data');
    
    // Verify cleanup
    console.log('Logout complete:', {
      hasToken: !!getAuthToken(),
      hasUserData: !!localStorage.getItem('user_data')
    });
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await authAPI.updateProfile(profileData);
      
      // Update user in context and localStorage
      const updatedUser = { ...user, ...response };
      setUser(updatedUser);
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile update failed' 
      };
    }
  };

  const refreshProfile = async () => {
    try {
      if (!token) return { success: false, error: 'No authentication token' };
      
      const response = await authAPI.getProfile();
      
      // Update user in context and localStorage with fresh data
      setUser(response);
      localStorage.setItem('user_data', JSON.stringify(response));
      
      return { success: true, user: response };
    } catch (error) {
      console.error('Profile refresh error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile refresh failed' 
      };
    }
  };

  // Fetch subscription status when user is authenticated
  const fetchSubscription = async () => {
    if (!token || !user) {
      setSubscription(null);
      return;
    }
    
    try {
      setSubscriptionLoading(true);
      const subscriptionData = await paymentAPI.getUserSubscription();
      setSubscription(subscriptionData);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // If error, assume no subscription
      setSubscription(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch subscription when user login state changes
  useEffect(() => {
    if (user && token) {
      fetchSubscription();
    } else {
      setSubscription(null);
    }
  }, [user, token]);

  // Check if user has access restrictions
  const hasRestrictedAccess = () => {
    // Admin users never have restricted access
    if (user?.role === 'admin') return false;
    
    if (!subscription) return false;
    return subscription.status === 'paused';
  };

  // Get allowed pages for restricted users
  const getAllowedPages = () => {
    if (!hasRestrictedAccess()) return null;
    
    return [
      '/profile/edit',
      '/explore'
    ];
  };

  // Check if current page is allowed for restricted users
  const isPageAllowed = (pathname) => {
    // Admin users can access all pages
    if (user?.role === 'admin') return true;
    
    if (!hasRestrictedAccess()) return true;
    
    const allowedPages = getAllowedPages();
    return allowedPages.some(page => pathname.startsWith(page));
  };

  // More robust authentication check with debugging
  const isAuthenticated = Boolean(user && user.id && token);
  
  // Debug authentication state
  useEffect(() => {
    console.log('AuthContext state update:', {
      isAuthenticated,
      hasUser: !!user,
      hasUserId: !!user?.id,
      hasToken: !!token,
      loading,
      userEmail: user?.email
    });
  }, [user, token, loading, isAuthenticated]);
  
  const value = {
    user,
    token,
    loading,
    subscription,
    subscriptionLoading,
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    fetchSubscription,
    hasRestrictedAccess,
    getAllowedPages,
    isPageAllowed,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};